// §IV board data — the VARIANTS from the Claude Design canon `R10 - §IV - canon F8`
// (project dd7b95d4, data-canon="R10-§IV-F8"), transcribed verbatim: carrier curve,
// carrierConfig / geometryClass / levelBand, members, carrierSourceLines, layerValues,
// carrierIdx, stage names/dates/BG1-BG2, and evidence quotes. Each board is a `view`
// SectionIV renders, exactly like §II/§III's boards.
//
// 31 boards: axis A (carrier layout) v01–v06 (real runs) + v07 (field-of-5), degradations
// v08, hard-fallback v09, fit-stress v10/v10b (N=15), minimum v11 (N=2), the WHERE IT
// STANDS NOW state bank v11b–v11g (states 6/7/8a–8e), axis B (curve shape) v12–v22, and
// v23/v24 — irregular on three answered points, the F8 state (s505.d1).
//
// `standing` is NOT hand-authored — it is the ENGINE's currentStanding
// (src/insights/render_keys.js) run over a current-stage vector, the shared detector §III
// also reads. For the real runs (v01/v04) the vector is the run's own last-stage layer
// values; for the synthetic state-bank boards it is a constructed current stage that the
// engine maps to the target state. sectionIVStanding.js turns that structural fact into
// the §IV register phrase. CD's canon used a hardcoded `split` shortcut for state 7; the
// engine expresses it structurally (a low/high cloud + 2 layers broken out the other way)
// and renders the same string. This keeps the §IV standing card honest, engine-proven.
//
// `labelMode` is CD `fitStageLabels`' full↔roman result, captured from a REAL browser with
// Newsreader loaded (getBBox measured): roman on v04/v05/v08/v10/v10b, full on the rest.
// SSR renders the declared mode; a client re-fit is cosmetic — the §III/§II contract.

import { currentStanding } from '../../insights/render_keys.js';
import { classifyGeometry } from '../../insights/geometry.js';

const LK = ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'];
// engine standing over a single current-stage vector (the wizard passport call)
const standingOf = (vals, active = LK) => currentStanding([{ values: vals }], active);

// ---- current-stage vectors → engine standing (one per distinct state) ---------------
// state 2: high cloud of six, being yourself broken out below (also = LV01's last stage).
const VS2 = { Vitality: 1, Security: 1, Growth: 1, Authorship: 1, Wanting: 0, Yourself: -2, Lasting: 1 };

// ---- stage sets (verbatim CD `st`/ST01…ST06/STB) -----------------------------------
const st = (a) => a.map((x) => ({ name: x[0], start: x[1], end: x[2], current: !!x[3], bg1: x[4] || null, bg2: x[5] || null }));

const ST01 = st([
  ['before', '09/2004', '06/2009', 0, ['drawn to something new'], 'on hope'],
  ['the first year', '07/2009', '08/2013', 0, ["couldn't let down", 'drawn to something new'], 'for someone'],
  ['the mirror year', '09/2013', '12/2016', 0, ['something broke or left'], 'out of duty'],
  ['everything for them', '01/2017', '04/2021', 0, ["couldn't let down"], 'out of duty'],
  ['still here', '05/2021', null, 1, ['tired of the old'], 'by inertia'],
]);
const LV01 = { Vitality: [1, 2, 1, 1, 1], Security: [1, 2, 1, 1, 1], Growth: [1, 2, 1, 1, 1], Authorship: [1, 2, 1, 1, 1], Wanting: [1, 2, 0, 0, 0], Yourself: [2, 2, -2, -2, -2], Lasting: [1, 2, 1, 1, 1] };

const ST02 = st([
  ['the bottom', '02/2016', '09/2018', 0, ['something broke or left'], 'on fear'],
  ['the first steady months', '10/2018', '05/2021', 0, ['had to'], 'for a goal'],
  ['climbing together', '06/2021', '11/2023', 0, ['drawn to something new'], 'for a goal'],
  ['standing on both', '12/2023', null, 1, ['wanted to prove it'], 'on hope'],
]);
const LV02 = { Vitality: [0, 1, 2, 3], Security: [0, 1, 2, 3] };

const ST03 = st([
  ['the bottom', '03/2015', '08/2017', 0, ['something broke or left'], 'on fear'],
  ['three fronts at once', '09/2017', '04/2020', 0, ['had to'], 'for a goal'],
  ['all three rising', '05/2020', '01/2024', 0, ['drawn to something new'], 'for a goal'],
  ['standing on all three', '02/2024', null, 1, ['wanted to prove it'], 'on hope'],
]);
const LV03 = { Vitality: [0, 1, 2, 3], Security: [0, 1, 2, 3], Growth: [0, 1, 2, 3] };

const ST04 = st([
  ['the ordinary years', '03/2009', '07/2014', 0, ['drawn to something new'], 'on hope'],
  ['the best of it', '08/2014', '04/2018', 0, ['wanted to prove it'], 'for a goal'],
  ['the year the floor gave out', '05/2018', '11/2020', 0, ['something broke or left'], 'on fear'],
  ['the slow climb back', '12/2020', '03/2024', 0, ['had to'], 'out of duty'],
  ['here now, rebuilt', '04/2024', null, 1, ['tired of the old'], 'on hope'],
]);
const LV04 = { Vitality: [1, 2, -2, 0, 2], Security: [1, 2, -2, 0, 2], Growth: [1, 2, -1, 0, 2], Authorship: [1, 2, -1, 1, 2], Wanting: [1, 2, -2, 0, 1], Yourself: [1, 2, -2, 0, 1], Lasting: [1, 2, -1, 1, 2] };

const ST05 = st([
  ['when the title was the person', '01/2007', '06/2011', 0, ['drawn to something new'], 'for a goal'],
  ['the peak', '07/2011', '02/2015', 0, ['wanted to prove it'], 'for a goal'],
  ['the last steady year', '03/2015', '09/2019', 0, ["couldn't let down"], 'out of duty'],
  ['the year they took the role', '10/2019', '08/2022', 0, ['something broke or left'], 'on fear'],
  ['solvent, and a stranger', '09/2022', null, 1, ['tired of the old'], 'by inertia'],
]);
const LV05 = { Security: [1, 2, 2, 2, 2], Yourself: [2, 3, 2, -2, -3] };

const ST06 = st([
  ['the first attempt', '03/2008', '11/2011', 0, ['drawn to something new'], 'for a goal'],
  ['the pivot', '12/2011', '07/2015', 0, ['something broke or left', 'drawn to something new'], 'on hope'],
  ['six half-starts', '08/2015', '09/2018', 0, ['tired of the old'], 'on fear'],
  ['still moving', '10/2018', '02/2023', 0, ['afraid to miss it'], 'on fear'],
  ['still searching', '03/2023', null, 1, ['tired of the old'], 'by inertia'],
]);

// synthetic stage set for the axis-B and fit variants
const STB = st([
  ['the early years', '05/2006', '01/2011', 0, ['drawn to something new'], 'on hope'],
  ['the turn', '02/2011', '10/2014', 0, ['something broke or left'], 'for someone'],
  ['the middle', '11/2014', '06/2018', 0, ['had to'], 'out of duty'],
  ['the long stretch', '07/2018', '12/2022', 0, ["couldn't let down"], 'for a goal'],
  ['now', '01/2023', null, 1, ['tired of the old'], 'by inertia'],
]);

const V = [];
const push = (o) => V.push(o);

push({ id: 'v01', screen: '§IV · 01 · ось A: одиночка', source: '01-singleton.json',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  standing: standingOf(VS2), // = LV01's own last stage: high cloud of six, being yourself below (state 2)
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 3, quote: "didn't let myself want anything just for me" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }] });

push({ id: 'v02', screen: '§IV · 02 · ось A: пара', source: '02-together-pair.json',
  carrierConfig: 'pair', members: ['Vitality', 'Security'], carrierSourceLines: ['Vitality', 'Security'], layerValues: LV02,
  carrier: [0, 1, 2, 3], geometryClass: 'monotonic-ascent', levelBand: 'high',
  carrierIdx: [0, 1, 2, 3], stages: ST02, labelMode: 'full',
  standing: standingOf({ Vitality: 3, Security: 3 }, ['Vitality', 'Security']),
  evidence: [{ stage: 0, quote: "wouldn't let myself plan past the end of the month" }, { stage: 1, quote: "didn't allow myself to call barely-holding a recovery" }, { stage: 2, quote: "wouldn't let myself trust that the ground would hold" }, { stage: 3, quote: 'I stopped treating being upright as borrowed time' }] });

push({ id: 'v03', screen: '§IV · 03 · ось A: тройка', source: '03-triplet.json',
  carrierConfig: 'triplet', members: ['Vitality', 'Security', 'Growth'], carrierSourceLines: ['Vitality', 'Security', 'Growth'], layerValues: LV03,
  carrier: [0, 1, 2, 3], geometryClass: 'monotonic-ascent', levelBand: 'high',
  carrierIdx: [0, 1, 2, 3], stages: ST03, labelMode: 'full',
  standing: standingOf({ Vitality: 3, Security: 3, Growth: 3 }, ['Vitality', 'Security', 'Growth']),
  evidence: [{ stage: 0, quote: "wouldn't let myself start until I could fix everything together" }, { stage: 1, quote: "didn't allow myself to celebrate the small steadying" }, { stage: 2, quote: "wouldn't let myself trust that they were holding as one thing" }, { stage: 3, quote: 'I stopped treating the body, the security, and the growing as separate repairs' }] });

push({ id: 'v04', screen: '§IV · 04 · ось A: поле (7 членов)', source: '04-field-level.json',
  carrierConfig: 'field', members: ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'], carrierSourceLines: [], layerValues: LV04,
  carrier: [1, 2, -1.5714, 0.2857, 1.7143], geometryClass: 'way-back', levelBand: 'high',
  carrierIdx: [2, 4], stages: ST04, labelMode: 'roman',
  standing: standingOf({ Vitality: 2, Security: 2, Growth: 2, Authorship: 2, Wanting: 1, Yourself: 1, Lasting: 2 }),
  evidence: [{ stage: 2, quote: "didn't let myself cry where anyone could see it" }, { stage: 4, quote: 'I stopped waiting to be the person I was before' }] });

push({ id: 'v05', screen: '§IV · 05 · ось A: раскол', source: '05-split-opposition.json',
  carrierConfig: 'split', members: ['Yourself', 'Security'], eyebrowMembers: ['Yourself', 'Security'], carrierSourceLines: ['Security'], layerValues: LV05,
  carrier: [2, 3, 2, -2, -3], geometryClass: 'peak-then-fall', levelBand: 'low',
  carrierIdx: [0, 3, 4], stages: ST05, labelMode: 'roman',
  // 7 touched: security broken out above a low cloud of five (+ one dangling non-outlier) → state 1
  standing: standingOf({ Security: 2, Yourself: -2, Vitality: -1, Growth: -1, Authorship: -2, Wanting: -1, Lasting: 0 }),
  evidence: [{ stage: 0, quote: "wouldn't let myself notice how much I needed to be needed" }, { stage: 3, quote: "didn't let anyone see that I no longer knew who I was" }, { stage: 4, quote: 'I stopped pretending the security is the same as being okay' }] });

push({ id: 'v06', screen: '§IV · 06 · ось A: разброс · M=0', source: '06-scatter.json',
  carrierConfig: 'scatter', members: ['Vitality', 'Security', 'Growth', 'Authorship', 'Wanting', 'Yourself', 'Lasting'], carrierSourceLines: [], layerValues: {},
  carrier: [0, 0, 0, 0, 0], geometryClass: 'plateau', levelBand: 'mid',
  carrierIdx: [], stages: ST06, labelMode: 'full',
  // 7 touched, no band of five → no cloud → scatter
  standing: standingOf({ Vitality: -3, Security: -2, Growth: -1, Authorship: 0, Wanting: 1, Yourself: 3, Lasting: 2 }), evidence: [] });

push({ id: 'v07', screen: '§IV · 07 · поле из пяти членов', source: 'synthetic',
  carrierConfig: 'field', members: ['Vitality', 'Security', 'Growth', 'Authorship', 'Lasting'],
  carrierSourceLines: ['Vitality', 'Security', 'Growth', 'Authorship', 'Lasting'],
  layerValues: { Vitality: [1, -1, -3, 0, 2], Security: [1, -2, -3, 1, 2], Growth: [2, -1, -2, 0, 3], Authorship: [0, -1, -3, -1, 2], Lasting: [1, 0, -3, 0, 1] },
  carrier: [1, -1, -2.8, 0, 2], geometryClass: 'V-shape', levelBand: 'high',
  carrierIdx: [2, 4], stages: STB, labelMode: 'full', synthetic: true,
  // 5 touched, all in a high band → all-together high
  standing: standingOf({ Vitality: 1, Security: 2, Growth: 2, Authorship: 2, Lasting: 2 }, ['Vitality', 'Security', 'Growth', 'Authorship', 'Lasting']),
  evidence: [{ stage: 2, quote: 'I kept the calendar full so no one would ask' }, { stage: 4, quote: 'I stopped explaining the gap in the middle' }] });

push({ id: 'v08', screen: '§IV · 08 · деградации', source: '01-singleton.json',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], labelMode: 'roman',
  stages: (() => {
    const s = ST01.map((o) => Object.assign({}, o));
    s[0] = Object.assign({}, s[0], { openStart: true });
    s[2] = Object.assign({}, s[2], { datesMissing: true });
    s[3] = Object.assign({}, s[3], { bg1: null });
    s[4] = Object.assign({}, s[4], { bg1: null, bg2: null });
    return s;
  })(),
  standing: standingOf(VS2),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }] });

push({ id: 'v09', screen: '§IV · 09 · hard fallback · 0 тронутых', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  standing: standingOf({}, []), // 0 touched → card dropped
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 3, quote: "didn't let myself want anything just for me" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v10', screen: '§IV · 10 · fit-стресс · N=15', source: 'synthetic',
  carrierConfig: 'pair', members: ['Authorship', 'Yourself'], carrierSourceLines: ['Authorship', 'Yourself'],
  layerValues: { Authorship: [1, 2, 1, 0, -1, 0, 1, 2, 1, 0, -1, -2, -1, 0, 1], Yourself: [0, 1, 2, 1, 0, -1, 0, 1, 2, 1, 0, -1, -2, -1, 0] },
  carrier: [0.5, 1.5, 1.5, 0.5, -0.5, -0.5, 0.5, 1.5, 1.5, 0.5, -0.5, -1.5, -1.5, -0.5, 0.5],
  geometryClass: 'oscillation', levelBand: 'mid',
  carrierIdx: [3, 8, 14], labelMode: 'roman',
  stages: Array.from({ length: 15 }, (_, i) => ({ name: ['the first room', 'the borrowed years', 'after the move', 'the long apprenticeship', 'the good stretch', 'the first crack', 'the year of holding on', 'the collapse', 'the quiet after', 'the slow return', 'the second start', 'the steady middle', 'the second crack', 'the rebuild', 'now'][i], start: `0${(i % 9) + 1}/${1996 + i * 2}`, end: i === 14 ? null : `0${(i % 9) + 1}/${1998 + i * 2}`, current: i === 14, bg1: ['tired of the old'], bg2: 'by inertia' })),
  standing: standingOf(VS2),
  evidence: [{ stage: 3, quote: 'I kept saying it was temporary' }, { stage: 8, quote: "didn't let myself count the years" }, { stage: 14, quote: 'I stopped waiting for the version of me that was coming' }],
  synthetic: true });

push({ id: 'v10b', screen: '§IV · 10b · потолок карточек · N=15 · M=9 → 6', source: 'synthetic',
  carrierConfig: 'pair', members: ['Authorship', 'Yourself'], carrierSourceLines: ['Authorship', 'Yourself'],
  layerValues: { Authorship: [1, 2, 1, 0, -1, 0, 1, 2, 1, 0, -1, -2, -1, 0, 1], Yourself: [0, 1, 2, 1, 0, -1, 0, 1, 2, 1, 0, -1, -2, -1, 0] },
  carrier: [0.5, 1.5, 1.5, 0.5, -0.5, -0.5, 0.5, 1.5, 1.5, 0.5, -0.5, -1.5, -1.5, -0.5, 0.5],
  geometryClass: 'oscillation', levelBand: 'mid',
  carrierIdx: [1, 3, 5, 7, 8, 10, 11, 13, 14], labelMode: 'roman',
  stages: Array.from({ length: 15 }, (_, i) => ({ name: ['the first room', 'the borrowed years', 'after the move', 'the long apprenticeship', 'the good stretch', 'the first crack', 'the year of holding on', 'the collapse', 'the quiet after', 'the slow return', 'the second start', 'the steady middle', 'the second crack', 'the rebuild', 'now'][i], start: `0${(i % 9) + 1}/${1996 + i * 2}`, end: i === 14 ? null : `0${(i % 9) + 1}/${1998 + i * 2}`, current: i === 14, bg1: ['tired of the old'], bg2: 'by inertia' })),
  standing: standingOf(VS2),
  evidence: [{ stage: 7, quote: "didn't let myself say how far down it went" }, { stage: 8, quote: "wouldn't let myself rest in the quiet" }, { stage: 10, quote: 'I kept the second start to myself' }, { stage: 11, quote: "didn't allow myself to call it steady" }, { stage: 13, quote: 'I stopped waiting for permission to rebuild' }, { stage: 14, quote: 'I stopped counting what I lost in the middle' }],
  synthetic: true });

push({ id: 'v11', screen: '§IV · 11 · минимум · N=2', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: { Yourself: [2, -2] },
  carrier: [2, -2], geometryClass: 'late-cliff', levelBand: 'low',
  carrierIdx: [1], labelMode: 'full',
  stages: st([['the years before', '06/2019', '01/2023', 0, ['something broke or left'], 'on fear'], ['since then', '02/2023', null, 1, ["couldn't let down"], 'out of duty']]),
  standing: standingOf(VS2),
  evidence: [{ stage: 1, quote: 'I stopped saying out loud what I wanted' }],
  synthetic: true });

// ---- states 6/7/8 of the WHERE IT STANDS NOW card (s390.d4 / s407.d3) ----
push({ id: 'v11b', screen: '§IV · 11b · WHERE IT STANDS NOW · состояние 7', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // two groups: five high, two at the floor → state 7
  standing: standingOf({ Vitality: 2, Security: 2, Growth: 2, Authorship: 2, Wanting: 2, Yourself: -2, Lasting: -2 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v11c', screen: '§IV · 11c · WHERE IT STANDS NOW · состояние 6 + зеркало 7', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // all touched sit mid, no outlier → state 6
  standing: standingOf({ Vitality: 0, Security: 0, Growth: 0, Authorship: 1, Wanting: 1, Yourself: 2, Lasting: 1 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v11d', screen: '§IV · 11d · WHERE IT STANDS NOW · состояние 8', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // mid cloud of six, vitality broken out above → state 8a (named)
  standing: standingOf({ Vitality: 3, Security: 0, Growth: 0, Authorship: 0, Wanting: 0, Yourself: 0, Lasting: 0 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v11e', screen: '§IV · 11e · состояние 8b · один слой ниже середины', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // mid cloud of six, security broken out below → state 8b (named)
  standing: standingOf({ Vitality: 0, Security: -3, Growth: 0, Authorship: 0, Wanting: 0, Yourself: 0, Lasting: 0 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v11f', screen: '§IV · 11f · состояние 8c · двое выше середины', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // mid cloud of five, two broken out above → state 8c (counted)
  standing: standingOf({ Vitality: 3, Security: 0, Growth: 3, Authorship: 0, Wanting: 0, Yourself: 0, Lasting: 0 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

push({ id: 'v11g', screen: '§IV · 11g · состояние 8e · один выше, один ниже', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Yourself'], carrierSourceLines: [], layerValues: LV01,
  carrier: [2, 2, -2, -2, -2], geometryClass: 'early-cliff', levelBand: 'low',
  carrierIdx: [2, 3, 4], stages: ST01, labelMode: 'full',
  // mid cloud of five, one above + one below → state 8e
  standing: standingOf({ Vitality: 3, Security: -3, Growth: 0, Authorship: 0, Wanting: 0, Yourself: 0, Lasting: 0 }),
  evidence: [{ stage: 2, quote: "wouldn't allow myself a single hour that wasn't for someone else" }, { stage: 4, quote: 'I stopped asking when I get to come back to myself' }],
  synthetic: true });

// six stages, only for oscillation: the class is structurally unreachable at five —
// it needs ≥2 interior maxima and ≥2 interior minima, and five points leave three interior slots.
const STB6 = st([
  ['the early years', '05/2006', '01/2011', 0, ['drawn to something new'], 'on hope'],
  ['the turn', '02/2011', '10/2014', 0, ['something broke or left'], 'for someone'],
  ['the middle', '11/2014', '06/2018', 0, ['had to'], 'out of duty'],
  ['the long stretch', '07/2018', '12/2020', 0, ["couldn't let down"], 'for a goal'],
  ['the first crack', '01/2021', '12/2022', 0, ['something broke or left'], 'out of duty'],
  ['now', '01/2023', null, 1, ['tired of the old'], 'by inertia'],
]);

// ---- axis B: curve shapes not covered by a real run (verbatim CD `bForms`) ----
const bForms = [
  ['monotonic-descent', 'low', [3, 2, 1, -1, -2], 'только вниз, каждый этап ниже предыдущего'],
  ['plateau', 'high', [2, 2, 2, 2, 2], 'ровная высоко — italic «A high orbit.»'],
  ['plateau', 'low', [-2, -2, -2, -2, -2], 'ровная низко — italic «Below sea level.»'],
  ['late-cliff', 'low', [1, 1, 1, 1, -3], 'долго ровно, обрыв в конце'],
  ['late-rise', 'high', [-1, -1, -1, -1, 3], 'долго ровно, подъём в конце'],
  ['early-rise', 'high', [-2, 2, 2, 2, 2], 'резкий подъём в начале, дальше ровно наверху'],
  ['oscillation', 'mid', [-1, 2, -1, 2, -1, 2], 'зигзаг, четыре разворота · N = 6: на пяти этапах класс недостижим — ноль из 16 807 кривых по перебору сетки', STB6],
  ['double-peak', 'mid', [0, 3, 0, 3, 1], 'два пика с провалом между'],
  ['double-valley', 'mid', [1, -3, 1, -3, 0], 'две ямы с подъёмом между'],
  ['high-held-collapse', 'low', [1, 3, 3, 3, -3], 'верх высокий, но живой (амплитуда головы 2), затем обвал. Ровная голова в класс не попадает: [2,2,2,2,−3] читается как late-cliff, и читается верно'],
  ['irregular', 'mid', [-1, 2, -1, 0, -2], 'формы нет: три смены направления, ни одна не складывается в форму'],
];
// two members whose mean is exactly the curve → pale source lines show on every axis-B form
function pairFrom(c) {
  const A = [], B = [];
  c.forEach((val, i) => { let d = i % 2 ? -1 : 1; if (val + Math.abs(d) > 3 || val - Math.abs(d) < -3) d = 0; A.push(val + d); B.push(val - d); });
  return { A, B };
}
bForms.forEach(([cls, band, curve, , stageSet], i) => {
  const n = 12 + i, pr = pairFrom(curve), STG = stageSet || STB;
  push({ id: 'v' + n, screen: `§IV · ${n} · ось B: ${cls}${cls === 'plateau' ? ' · ' + band : ''}`, source: 'synthetic',
    carrierConfig: 'pair', members: ['Vitality', 'Yourself'], carrierSourceLines: ['Vitality', 'Yourself'], layerValues: { Vitality: pr.A, Yourself: pr.B },
    carrier: curve, geometryClass: cls, levelBand: band,
    carrierIdx: [1, 4], stages: STG, labelMode: 'full',
    standing: standingOf(VS2),
    evidence: [{ stage: 1, quote: "didn't let myself say it was going wrong" }, { stage: 4, quote: 'I stopped pretending it would come back on its own' }],
    synthetic: true });
});

// ---- v23 / v24 · irregular on three answered points (canon F8, s505.d1) ---------------------
//
// The state the whole F8 release exists for, and until these two entries landed it was covered by
// NOTHING: the golden held 31 boards while this fixture held 29, and the parity suite iterates
// THIS list. A board CD ships that we do not transcribe is a board no test ever renders — green
// on 29 says nothing about the other two. Same shape as the gap I flagged to CD before F7 (no §IV
// board existed at three stages at all); it reappeared one level down, on our side.
//
// Both carry the same carrier [1,-3,-2] on purpose, so the pair differs only by carrier config —
// singular subject vs plural — which is exactly what the neutral predicate had to be read against.
// `standing` is engine-derived like every other board here, not transcribed from CD's file: at
// this spread no cloud forms, so both print "nothing holds together — each layer on its own."
// That match is what rejected F7, where the same board declared a cloud of four its own numbers
// did not contain.
const ST3 = st([
  ['the early years', '05/2006', '01/2011', 0, ['drawn to something new'], 'on hope'],
  ['the turn', '02/2011', '10/2014', 0, ['something broke or left'], 'for someone'],
  ['now', '11/2014', null, 1, ['tired of the old'], 'by inertia'],
]);
const EV3 = [
  { stage: 0, quote: "didn't let myself say it was going wrong" },
  { stage: 2, quote: 'I stopped pretending it would come back on its own' },
];
push({ id: 'v23', screen: '§IV · 23 · irregular · N=3 · ед.', source: 'synthetic',
  carrierConfig: 'singleton', members: ['Growth'], carrierSourceLines: [],
  layerValues: { Growth: [1, -3, -2], Yourself: [-1, 3, -3] },
  carrier: [1, -3, -2], levelBand: 'low',
  carrierIdx: [0, 2], stages: ST3, labelMode: 'full',
  standing: standingOf({ Growth: -2, Yourself: -3 }, ['Growth', 'Yourself']),
  evidence: EV3, synthetic: true });

push({ id: 'v24', screen: '§IV · 24 · irregular · N=3 · мн. (раскол)', source: 'synthetic',
  carrierConfig: 'split', members: ['Vitality', 'Security'], eyebrowMembers: ['Vitality', 'Security'], carrierSourceLines: ['Security'],
  layerValues: { Vitality: [1, -3, -2], Security: [2, 3, 2], Authorship: [-1, -3, 3], Wanting: [-2, 2, 3], Yourself: [2, 2, -2] },
  carrier: [1, -3, -2], levelBand: 'low',
  carrierIdx: [0, 2], stages: ST3, labelMode: 'full',
  standing: standingOf({ Vitality: -2, Security: 2, Authorship: 3, Wanting: 3, Yourself: -2 },
    ['Vitality', 'Security', 'Authorship', 'Wanting', 'Yourself']),
  evidence: EV3, synthetic: true });

// evidence-card ceiling = 6 (s378.d2): keep the six most recent; older carriers drop out
// of everything shown (cards, "{M} stages…" subhead, numbered points) and render as muted
// points, alongside the non-carriers. No pagination.
const EV_CAP = 6;
V.forEach((v) => {
  if (v.carrierIdx.length > EV_CAP) {
    v.carrierDropped = v.carrierIdx.slice(0, v.carrierIdx.length - EV_CAP);
    v.carrierIdx = v.carrierIdx.slice(-EV_CAP);
  }
});

// ---- geometryClass is DERIVED, never declared (s497, CD's diagnosis) ----------------------
//
// It used to be a hand-typed string on every board, transcribed from CD's canon. That let a board
// assert a state the engine cannot produce — and parity stayed byte-green, because BOTH sides
// carried the same hand-typed string: CD's file and this fixture. An audit found six such boards
// out of 29, one of which (v11, `late-cliff` on a two-point carrier) had been wrong for six weeks.
//
// CD proposed inlining the classifier into his canon file so the class could not be typed at all.
// Right diagnosis, wrong place for it here: that puts a copy of our engine inside a design file,
// so every engine change moves his bytes, forces a new F number and a golden recapture even when
// nothing on the page moves — and two engine changes are already queued.
//
// Deriving on OUR side gets the same guarantee for free. Once this fixture stops carrying the
// string, a board where CD's file and our engine disagree renders a different sentence, and the
// EXISTING parity suite goes red. The class drives three fields (page title predicate, the italic
// phrase under the chart, THE SHAPE), so the disagreement shows loudly rather than silently.
//
// `levelBand` stays hand-authored for now: it is only read for the plateau italic, and changing
// two things at once would blur which one moved.
V.forEach((v) => {
  if (!Array.isArray(v.carrier) || !v.carrier.length) return;
  const fractional = v.carrier.some((x) => !Number.isInteger(x));
  v.geometryClass = classifyGeometry(v.carrier, fractional).geometryClass;
});

export const IV_BOARDS = V;
// the DOM-parity subset (CD's rendered section reproduced byte-for-byte)
export const IV_PARITY_BOARDS = IV_BOARDS.filter((b) => b.parity !== false);
